import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { AideEnBulle, Badge, Chargement, EnTetePage, Erreur, Infobulle } from '../components/Elements';
import { ChoixMultiple } from '../components/ChoixMultiple';
import { IconePresences } from '../components/IconePresences';
import { BandeauAnneeFigee } from '../components/SelecteurAnnee';
import { useAnnee } from '../hooks/useAnnee';
import { capitaliser, formaterDate, formaterHeure, formaterMois, libelleJour } from '../lib/format';
import type {
  LigneListePresence,
  ListePresence,
  SectionListePresence,
  StatutPresence,
} from '../types';

/**
 * LISTE DE PRESENCE (2026-10-13, decisions de Georges sur maquette) :
 * tous les creneaux suivis de l'annee, sur le MOIS affiche. Une section
 * par creneau pour un cours de groupe ; une section par activite pour
 * les cours individuels, chaque eleve sur son propre jour. Les colonnes
 * sont les seances du calendrier du mois pour les jours affiches ; une
 * case ne se pose que la ou la ligne a cours. La saisie est celle de la
 * feuille d'un creneau : un clic enregistre.
 *
 * Les trois filtres se deduisent les uns des autres sur les sections
 * recues, comme sur l'ecran Creneaux et le rapprochement.
 */
type Etat = StatutPresence | 'vide' | 'futur' | 'hors';

const STYLES: Record<Etat, string> = {
  present: 'border-emerald-200 bg-emerald-50 text-emerald-600',
  absent: 'border-red-200 bg-red-50 text-red-600',
  vide: 'border-dashed border-encre-300 bg-white text-encre-400',
  futur: 'border-encre-100 bg-encre-50 text-encre-300',
  hors: 'border-encre-200 bg-encre-100 text-encre-400',
};
/** En mode « absence seulement », une case vide vaut present : trait plein, plus pale. */
const STYLE_PRESENT_IMPLICITE = 'border-emerald-100 bg-emerald-50/60 text-emerald-300';
const GLYPHES: Record<Etat, string> = { present: '✓', absent: '✕', vide: '', futur: '', hors: '—' };

type CleFiltre = 'professeurs' | 'jours' | 'activites';
const CLES: CleFiltre[] = ['professeurs', 'jours', 'activites'];

/** Le mois courant a Paris, « AAAA-MM ». */
function moisCourant(): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' })
    .format(new Date())
    .slice(0, 7);
}
function bornesDuMois(mois: string): { du: string; au: string } {
  const [an, m] = mois.split('-').map(Number) as [number, number];
  const dernier = new Date(Date.UTC(an, m, 0)).getUTCDate();
  return { du: `${mois}-01`, au: `${mois}-${String(dernier).padStart(2, '0')}` };
}
function moisDecale(mois: string, delta: number): string {
  const [an, m] = mois.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(an, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function horsInscription(ligne: LigneListePresence, date: string): boolean {
  if (ligne.demarrage_differe && ligne.date_debut && date < ligne.date_debut) return true;
  if (ligne.date_fin_anticipee && date > ligne.date_fin_anticipee) return true;
  return false;
}

export function ListePresencePage() {
  const { entiteActive, peut } = useAuth();
  const annee = useAnnee();
  const cle = entiteActive?.id ?? '';
  const [mois, setMois] = useState(moisCourant);
  const [filtres, setFiltres] = useState<Record<CleFiltre, string[]>>({
    professeurs: [],
    jours: [],
    activites: [],
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [local, setLocal] = useState<Record<string, Record<string, StatutPresence>>>({});
  const [enCours, setEnCours] = useState(false);

  const { du, au } = bornesDuMois(mois);
  const liste = useRessource(
    () =>
      annee.anneeId
        ? api.get<ListePresence>('/api/presences/liste', { anneeId: annee.anneeId, du, au })
        : Promise.resolve(null),
    [cle, annee.anneeId, du, au],
  );

  if (liste.chargement && !liste.donnees) return <Chargement />;
  if (liste.erreur) return <Erreur message={liste.erreur} onReessayer={liste.recharger} />;
  const donnees = liste.donnees;
  if (!donnees) return <Chargement />;
  const toutes = donnees.sections;

  // LES TROIS LISTES SE DEDUISENT LES UNES DES AUTRES, sur les sections de
  // l'annee. Une section d'activite individuelle porte plusieurs jours et
  // plusieurs professeurs : elle passe des qu'une de ses lignes passe.
  const joursDe = (s: SectionListePresence) => [...new Set(s.lignes.map((l) => String(l.jour)))];
  const passe = (s: SectionListePresence, f: typeof filtres, sauf: CleFiltre | null = null) =>
    (sauf === 'activites' || f.activites.length === 0 || f.activites.includes(s.activite_id)) &&
    (sauf === 'jours' || f.jours.length === 0 || joursDe(s).some((j) => f.jours.includes(j))) &&
    (sauf === 'professeurs' ||
      f.professeurs.length === 0 ||
      s.encadrants.some((e) => f.professeurs.includes(e.personnel_id)));
  const poserFiltre = (cleFiltre: CleFiltre, valeurs: string[]) => {
    const suite = { ...filtres, [cleFiltre]: valeurs };
    for (const autre of CLES) {
      if (autre === cleFiltre || suite[autre].length === 0) continue;
      suite[autre] = suite[autre].filter((valeur) =>
        toutes.some((s) => passe(s, { ...suite, [autre]: [valeur] })),
      );
    }
    setFiltres(suite);
  };
  const options = (sauf: CleFiltre) => toutes.filter((s) => passe(s, filtres, sauf));
  const activitesProposees = [
    ...new Map(options('activites').map((s) => [s.activite_id, s.activite_libelle])),
  ].sort((a, b) => a[1].localeCompare(b[1]));
  const joursProposes = [...new Set(options('jours').flatMap(joursDe))]
    .map(Number)
    .sort((a, b) => a - b);
  const encadrantsProposes = [
    ...new Map(
      options('professeurs').flatMap((s) => s.encadrants.map((e) => [e.personnel_id, e] as const)),
    ).values(),
  ].sort((a, b) => a.prenom_usuel.localeCompare(b.prenom_usuel));
  const libelleEncadrant = (e: (typeof encadrantsProposes)[number]) =>
    encadrantsProposes.filter((x) => x.prenom_usuel === e.prenom_usuel).length > 1
      ? `${e.prenom_usuel} ${e.nom ?? ''}`.trim()
      : e.prenom_usuel;

  // Les sections affichees, et dans chacune les seules lignes qui passent
  // le filtre Jour (une activite individuelle melange les jours).
  const sections = toutes
    .filter((s) => passe(s, filtres))
    .map((s) => ({
      ...s,
      lignes: s.lignes.filter(
        (l) => filtres.jours.length === 0 || filtres.jours.includes(String(l.jour)),
      ),
    }))
    .filter((s) => s.lignes.length > 0);
  // Les colonnes : les seances de la periode dont le jour est porte par
  // une ligne affichee — un mercredi sans cours affiche n'a pas sa colonne.
  const joursAffiches = new Set(sections.flatMap((s) => s.lignes.map((l) => l.jour)));
  const dates = donnees.dates.filter((d) => joursAffiches.has(d.jour));
  const peutSaisir = peut('presence.saisir') && annee.modifiable;

  const pointageDe = (ligne: LigneListePresence, date: string): StatutPresence | undefined => {
    const corrige = local[ligne.inscription_id];
    if (corrige && date in corrige) return corrige[date];
    return ligne.presences[date];
  };
  const etatDe = (
    section: SectionListePresence,
    ligne: LigneListePresence,
    colonne: { date: string; passee: boolean },
  ): Etat => {
    if (horsInscription(ligne, colonne.date)) return 'hors';
    if (!colonne.passee) return 'futur';
    const pointe = pointageDe(ligne, colonne.date);
    if (pointe) return pointe;
    return section.mode === 'absence' ? 'present' : 'vide';
  };
  const pointer = async (
    ligne: LigneListePresence,
    date: string,
    statut: StatutPresence | null,
  ) => {
    setErreur(null);
    setEnCours(true);
    try {
      await api.put('/api/presences', {
        inscriptionId: ligne.inscription_id,
        dateSeance: date,
        statut,
      });
      setLocal((avant) => ({
        ...avant,
        [ligne.inscription_id]: {
          ...(avant[ligne.inscription_id] ?? {}),
          [date]: statut as StatutPresence,
        },
      }));
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
      liste.recharger();
    } finally {
      setEnCours(false);
    }
  };
  const suivant = (
    section: SectionListePresence,
    ligne: LigneListePresence,
    date: string,
  ): StatutPresence | null => {
    const pointe = pointageDe(ligne, date) ?? null;
    if (section.mode === 'absence') return pointe === 'absent' ? null : 'absent';
    if (pointe === null) return 'present';
    if (pointe === 'present') return 'absent';
    return null;
  };
  const auClic = (
    section: SectionListePresence,
    ligne: LigneListePresence,
    colonne: { date: string; passee: boolean },
  ) => {
    if (!peutSaisir || enCours) return;
    const etat = etatDe(section, ligne, colonne);
    if (etat === 'hors' || etat === 'futur') return;
    void pointer(ligne, colonne.date, suivant(section, ligne, date(colonne)));
  };
  const date = (c: { date: string }) => c.date;

  const absencesDe = (section: SectionListePresence, ligne: LigneListePresence) =>
    dates.filter((c) => c.jour === ligne.jour && etatDe(section, ligne, c) === 'absent').length;
  const bilanDe = (
    section: SectionListePresence,
    colonne: { date: string; jour: number; passee: boolean },
  ) => {
    if (!colonne.passee) return '';
    const lignes = section.lignes.filter((l) => l.jour === colonne.jour);
    if (lignes.length === 0) return '';
    let presents = 0;
    let absents = 0;
    for (const l of lignes) {
      const etat = etatDe(section, l, colonne);
      if (etat === 'present') presents += 1;
      if (etat === 'absent') absents += 1;
    }
    return `${presents} · ${absents}`;
  };

  const nbInscrits = sections.reduce((n, s) => n + s.lignes.length, 0);
  const nbGroupes = sections.filter((s) => !s.individuel).length;
  const nbIndividuelles = sections.filter((s) => s.individuel).length;
  const passees = dates.filter((d) => d.passee).length;
  const filtresActifs = CLES.some((c) => filtres[c].length > 0);

  return (
    <div>
      <EnTetePage
        titre={
          <>
            Liste de présence
            {/* L'explication en infobulle plutot qu'en sous-titre (2026-10-13),
                par le bouton « ? » commun a tous les ecrans. */}
            <AideEnBulle texte="Tous les inscrits des créneaux dont le type d’activité suit les présences, sur le mois affiché. Un clic sur une case enregistre." />
          </>
        }
      />
      {annee.annee && !annee.modifiable && <BandeauAnneeFigee annee={annee.annee} />}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex h-9 items-center overflow-hidden rounded-md border border-encre-300 bg-white">
          <button
            type="button"
            className="h-9 w-8 text-encre-600 hover:bg-encre-50"
            aria-label="Mois précédent"
            onClick={() => setMois(moisDecale(mois, -1))}
          >
            ‹
          </button>
          <span className="min-w-36 border-x border-encre-200 px-3 text-center text-sm font-medium leading-9">
            {capitaliser(formaterMois(mois))}
          </span>
          <button
            type="button"
            className="h-9 w-8 text-encre-600 hover:bg-encre-50"
            aria-label="Mois suivant"
            onClick={() => setMois(moisDecale(mois, 1))}
          >
            ›
          </button>
        </div>
        <ChoixMultiple
          libelle="Professeur"
          options={encadrantsProposes.map((e) => ({
            valeur: e.personnel_id,
            libelle: libelleEncadrant(e),
          }))}
          valeurs={filtres.professeurs}
          onChanger={(valeurs) => poserFiltre('professeurs', valeurs)}
        />
        <ChoixMultiple
          libelle="Jour"
          options={joursProposes.map((jour) => ({
            valeur: String(jour),
            libelle: libelleJour(jour),
          }))}
          valeurs={filtres.jours}
          onChanger={(valeurs) => poserFiltre('jours', valeurs)}
        />
        <ChoixMultiple
          libelle="Activité"
          options={activitesProposees.map(([id, libelle]) => ({ valeur: id, libelle }))}
          valeurs={filtres.activites}
          onChanger={(valeurs) => poserFiltre('activites', valeurs)}
        />
        {filtresActifs && (
          <button
            type="button"
            className="text-xs text-encre-500 underline"
            onClick={() => setFiltres({ professeurs: [], jours: [], activites: [] })}
          >
            Tout effacer
          </button>
        )}
        <span className="ml-auto text-xs text-encre-500">
          {nbGroupes} créneau(x) de groupe · {nbIndividuelles} activité(s) individuelle(s) ·{' '}
          {nbInscrits} inscrit(s) · {passees} séance(s) passée(s) sur {dates.length} dans la période
        </span>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-encre-700">
        <Legende classe={STYLES.present} glyphe="✓" libelle="présent" />
        <Legende classe={STYLES.absent} glyphe="✕" libelle="absent" />
        <Legende classe={STYLES.vide} glyphe="" libelle="non saisi" />
        <Legende
          classe={STYLE_PRESENT_IMPLICITE}
          glyphe="✓"
          libelle="présent, non pointé (absence seulement)"
        />
        <Legende classe={STYLES.futur} glyphe="" libelle="à venir" />
        <Legende classe={STYLES.hors} glyphe="—" libelle="hors inscription" />
      </div>

      {erreur && (
        <div className="mb-3">
          <Erreur message={erreur} />
        </div>
      )}

      {toutes.length === 0 ? (
        <p className="text-sm text-encre-500">
          Aucun créneau de cette année n’a de type d’activité qui suit les présences. Le réglage se
          fait sur la fiche du type d’activité, champ « Suivi des présences ».
        </p>
      ) : sections.length === 0 || dates.length === 0 ? (
        <p className="text-sm text-encre-500">
          Aucune séance pour ces filtres sur {formaterMois(mois)}.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-encre-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-encre-50">
                <th className="sticky left-0 z-10 w-72 min-w-72 border-b border-r border-encre-200 bg-encre-50 px-3 py-2 text-left font-medium">
                  Créneau · adhérent
                </th>
                {dates.map((colonne) => (
                  <th
                    key={colonne.date}
                    className={`w-14 min-w-14 border-b border-encre-200 px-0.5 py-1.5 text-center font-medium ${
                      colonne.passee ? 'text-encre-900' : 'text-encre-400'
                    }`}
                  >
                    <div className="text-[10px] font-normal text-encre-400">
                      {libelleJour(colonne.jour).slice(0, 3).toLowerCase()}.
                    </div>
                    <div className="tabulaire">{formaterDate(colonne.date).slice(0, 5)}</div>
                  </th>
                ))}
                <th className="w-14 min-w-14 border-b border-l border-encre-200 px-2 py-2 text-right font-medium">
                  Abs.
                </th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => (
                <SectionTableau
                  key={section.cle}
                  section={section}
                  dates={dates}
                  etatDe={etatDe}
                  absencesDe={absencesDe}
                  bilanDe={bilanDe}
                  peutSaisir={peutSaisir}
                  onClic={auClic}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-encre-500">
        Une section par créneau pour les cours de groupe, une par activité pour les cours
        individuels. Un clic passe de non saisi à présent, puis absent, puis non saisi ; en «
        absence seulement », un clic pose l’absence et le second l’efface. Les séances à venir et
        les cases hors inscription ne se saisissent pas.
      </p>
    </div>
  );
}

function SectionTableau({
  section,
  dates,
  etatDe,
  absencesDe,
  bilanDe,
  peutSaisir,
  onClic,
}: {
  section: SectionListePresence;
  dates: { date: string; jour: number; passee: boolean }[];
  etatDe: (
    s: SectionListePresence,
    l: LigneListePresence,
    c: { date: string; passee: boolean },
  ) => Etat;
  absencesDe: (s: SectionListePresence, l: LigneListePresence) => number;
  bilanDe: (s: SectionListePresence, c: { date: string; jour: number; passee: boolean }) => string;
  peutSaisir: boolean;
  onClic: (
    s: SectionListePresence,
    l: LigneListePresence,
    c: { date: string; passee: boolean },
  ) => void;
}) {
  const totalAbsences = section.lignes.reduce((n, l) => n + absencesDe(section, l), 0);
  const creneau = section.creneau;
  return (
    <>
      <tr className="bg-blue-50">
        <th className="sticky left-0 z-10 whitespace-nowrap border-b border-r border-t border-encre-200 bg-blue-50 px-3 py-1.5 text-left font-semibold">
          <span className="flex items-baseline gap-2">
            <span>{section.activite_libelle}</span>
            {creneau ? (
              <>
                <span className="font-normal text-encre-700">
                  {libelleJour(creneau.jour)} {formaterHeure(creneau.heure_debut)}
                </span>
                <span className="font-normal text-encre-500">
                  {[creneau.encadrant, creneau.lieu_code].filter(Boolean).join(' · ')}
                </span>
                <Infobulle texte="Feuille de présence du créneau" className="inline-flex">
                  <Link
                    to={`/creneaux/${creneau.id}/presences`}
                    className="inline-flex items-center rounded border border-blue-200 bg-white px-1.5 py-0.5 leading-none text-blue-700 hover:border-blue-400"
                    aria-label="Feuille de présence du créneau"
                  >
                    <IconePresences />
                  </Link>
                </Infobulle>
              </>
            ) : (
              <span className="font-normal text-encre-500">
                cours individuels · chaque élève sur son créneau
              </span>
            )}
            {section.mode === 'absence' && <Badge ton="contour">absence seulement</Badge>}
          </span>
        </th>
        {dates.map((colonne) => (
          <td
            key={colonne.date}
            className="tabulaire border-b border-t border-encre-200 px-0.5 py-1 text-center text-[10px] text-blue-700"
          >
            {bilanDe(section, colonne)}
          </td>
        ))}
        <td className="tabulaire border-b border-l border-t border-encre-200 px-2 py-1 text-right text-[10px] text-encre-500">
          {totalAbsences} abs.
        </td>
      </tr>
      {section.lignes.map((ligne) => {
        const absences = absencesDe(section, ligne);
        return (
          <tr key={ligne.inscription_id}>
            <th className="sticky left-0 z-10 whitespace-nowrap border-b border-r border-encre-100 bg-white py-1 pl-7 pr-3 text-left font-normal">
              <span className="flex items-baseline gap-1.5">
                <Link
                  to={`/adherents/${ligne.personne_id}`}
                  className="font-medium text-encre-900 hover:underline"
                >
                  {ligne.nom.toUpperCase()} {ligne.prenom}
                </Link>
                {section.individuel && (
                  <Badge>
                    {libelleJour(ligne.jour)} {formaterHeure(ligne.heure_debut)}
                    {ligne.encadrant ? ` · ${ligne.encadrant}` : ''}
                    {ligne.lieu_code ? ` · ${ligne.lieu_code}` : ''}
                  </Badge>
                )}
                {ligne.forfait_essai && <Badge ton="contour">essai</Badge>}
                {ligne.demarrage_differe && ligne.date_debut && (
                  <Badge>depuis le {formaterDate(ligne.date_debut)}</Badge>
                )}
                {ligne.date_fin_anticipee && (
                  <Badge ton="danger">arrêt le {formaterDate(ligne.date_fin_anticipee)}</Badge>
                )}
              </span>
            </th>
            {dates.map((colonne) => {
              if (colonne.jour !== ligne.jour) {
                return <td key={colonne.date} className="border-b border-encre-100" />;
              }
              const etat = etatDe(section, ligne, colonne);
              const saisissable = peutSaisir && etat !== 'hors' && etat !== 'futur';
              const implicite =
                section.mode === 'absence' && etat === 'present' && !ligne.presences[colonne.date];
              return (
                <td
                  key={colonne.date}
                  className="border-b border-encre-100 px-0.5 py-0.5 text-center"
                >
                  <button
                    type="button"
                    disabled={!saisissable}
                    aria-label={`${ligne.prenom} ${ligne.nom}, ${formaterDate(colonne.date)} : ${etat}`}
                    className={`h-6 w-10 rounded border text-sm leading-none ${
                      implicite ? STYLE_PRESENT_IMPLICITE : STYLES[etat]
                    } ${saisissable ? 'hover:border-encre-400' : 'cursor-default'}`}
                    onClick={() => onClic(section, ligne, colonne)}
                  >
                    {GLYPHES[etat]}
                  </button>
                </td>
              );
            })}
            <td
              className={`tabulaire border-b border-l border-encre-100 px-2 py-1 text-right ${
                absences > 0 ? 'text-red-600' : 'text-encre-400'
              }`}
            >
              {absences}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function Legende({ classe, glyphe, libelle }: { classe: string; glyphe: string; libelle: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded border text-xs leading-none ${classe}`}
      >
        {glyphe}
      </span>
      {libelle}
    </span>
  );
}
